import { useEffect, useState } from "react";
import {
  useCustomers,
  useCustomerIdentity,
  type CustomerListRow,
  type CustomerIdentity,
} from "@/api/customers";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCustomerName, getCustomerEmail } from "@/lib/safe-access";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useFormContext } from "react-hook-form";

interface CustomerSearchProps {
  value?: string | null;
  onValueChange: (value: string) => void;
  onCustomerSelect?: (customer: CustomerIdentity | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
}
export function CustomerSearch({
  value,
  onValueChange,
  onCustomerSelect,
  placeholder = "Select customer...",
  className,
  disabled = false,
  allowClear = false,
}: CustomerSearchProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [request, setRequest] = useState({ search: "", page: 1 });
  useEffect(() => {
    if (searchTerm === request.search) return;
    const timer = setTimeout(
      () => setRequest({ search: searchTerm, page: 1 }),
      300
    );
    return () => clearTimeout(timer);
  }, [searchTerm, request.search]);
  const query = useCustomers({ ...request, limit: 20, enabled: open });
  const scope = JSON.stringify([query.identity, request.search]);
  const [loaded, setLoaded] = useState<{
    scope: string;
    rows: CustomerListRow[];
  }>({ scope, rows: [] });
  useEffect(() => {
    if (query.identity === null) {
      setLoaded({ scope, rows: [] });
      return;
    }
    if (
      !query.isSuccess ||
      !query.data ||
      query.data.pagination.page !== request.page
    )
      return;
    const data = query.data;
    setLoaded((previous) => {
      const rows =
        previous.scope === scope && request.page > 1 ? previous.rows : [];
      return {
        scope,
        rows: [
          ...new Map(
            [...rows, ...data.data].map((row) => [row.id, row])
          ).values(),
        ],
      };
    });
  }, [scope, query.identity, query.isSuccess, query.data, request.page]);
  const rows = loaded.scope === scope ? loaded.rows : [];
  const selectedRow = rows.find((row) => row.id === value);
  const selectedQuery = useCustomerIdentity(
    value && !selectedRow ? value : undefined
  );
  const selectedCustomer = selectedRow ?? selectedQuery.data;
  useEffect(() => {
    onCustomerSelect?.(selectedCustomer);
  }, [selectedCustomer, onCustomerSelect]);
  const label = !value
    ? placeholder
    : selectedCustomer
      ? getCustomerName(selectedCustomer)
      : selectedQuery.error
        ? "Could not load customer"
        : "Loading customer...";
  const loadMore = () => {
    if (
      query.data?.pagination.hasNextPage &&
      !query.isFetching &&
      !query.error &&
      !query.isCoolingDown
    )
      setRequest((current) => ({ ...current, page: current.page + 1 }));
  };
  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-label={label}
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            {label}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Filter customers..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            {query.error && (
              <div role="alert" className="p-2 text-sm text-destructive">
                Could not load customers.
                {rows.length > 0 && " Showing previously loaded results."}
                <Button
                  variant="link"
                  disabled={query.isCoolingDown || query.isFetching}
                  onClick={() => void query.refetch()}
                >
                  Retry
                </Button>
              </div>
            )}
            {query.isFetching && (
              <p role="status" className="p-2 text-sm">
                Loading customers...
              </p>
            )}
            {!query.error && !query.isFetching && rows.length === 0 && (
              <CommandEmpty>No customers found.</CommandEmpty>
            )}
            <CommandGroup
              className="max-h-[300px] overflow-y-auto"
              onScroll={(event) => {
                const list = event.currentTarget;
                if (list.scrollHeight - list.scrollTop - list.clientHeight < 10)
                  loadMore();
              }}
            >
              {allowClear && (
                <CommandItem
                  value="no-customer"
                  onSelect={() => {
                    onValueChange("");
                    setOpen(false);
                  }}
                >
                  No customer
                </CommandItem>
              )}
              {rows.map((customer) => (
                <CommandItem
                  key={customer.id}
                  value={customer.id}
                  onSelect={() => {
                    onValueChange(customer.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      customer.id === value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span>
                      {getCustomerName(customer)}
                      {customer.businessAccount && (
                        <span className="ml-2 text-sm text-gray-500">
                          ({customer.businessAccount.companyName})
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getCustomerEmail(customer)}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {query.data?.pagination.hasNextPage && (
              <Button
                variant="ghost"
                className="w-full"
                disabled={
                  query.isFetching || query.isCoolingDown || !!query.error
                }
                onClick={loadMore}
              >
                Load more
              </Button>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {value && !selectedRow && selectedQuery.error && (
        <div role="alert" className="text-sm text-destructive">
          Could not load selected customer.
          <Button
            variant="link"
            disabled={selectedQuery.isCoolingDown || selectedQuery.isFetching}
            onClick={() => void selectedQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      )}
    </div>
  );
}

// Form-specific wrapper that integrates with react-hook-form
interface FormCustomerSearchProps {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onCustomerSelect?: (customer: CustomerIdentity | undefined) => void;
}

export function FormCustomerSearch({
  name,
  label = "Customer",
  placeholder,
  className,
  disabled,
  onCustomerSelect,
}: FormCustomerSearchProps) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <CustomerSearch
              value={field.value as string}
              onValueChange={(value) => field.onChange(value)}
              onCustomerSelect={onCustomerSelect}
              placeholder={placeholder}
              className={className}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
